import * as React from 'react';
import {matchPath} from 'react-router-dom';
// @ts-expect-error
import type {ContextRouter} from 'react-router-dom';

import {safelyGetFilterByKey} from 'nutshell-core/filter';
import type {User, FilterObject} from 'nutshell-core/types';

import {colors} from 'shells/colors/colors';
import {InstagramColoredIcon} from 'shells/icon/instagram-colored-icon';
import {MessengerColoredIcon} from 'shells/icon/messenger-colored-icon';
import {NavIconChatIcon} from 'shells/icon/navigation/nav-icon-chat';
import {AgentsIcon} from 'shells/icon/svg/agents-icon';
import {ChatBubbleDoubleRegularIcon} from 'shells/icon/svg/chat-bubble-double-regular-icon';
import {ChatRegularOptionsIcon} from 'shells/icon/svg/chat-regular-options-icon';
import {ComputerMobileRegularIcon} from 'shells/icon/svg/computer-mobile-regular-icon';
import {EnvelopeNewIcon} from 'shells/icon/svg/envelope-new-icon';
import {LeadChevronRegularIcon} from 'shells/icon/svg/lead-chevron-regular-icon';
import {MobilePhoneRegularIcon} from 'shells/icon/svg/mobile-phone-regular-icon';
// @ts-expect-error - imported from .js/.jsx file
import {TeamIcon} from 'shells/icon/svg/team-icon';
import {ViewExploreIcon} from 'shells/icon/svg/view-explore-icon';
import {WhatsAppIcon} from 'shells/icon/svg/whatsapp-icon';
import {WrenchRegularIcon} from 'shells/icon/svg/wrench-regular-icon';
import {SecondarySidebar} from 'shells/navigation/secondary-sidebar/secondary-sidebar';
import type {NavItem} from 'shells/navigation/types';

import {useGetAgents} from '../inbox/chat/graphql/hooks/use-get-agents';
import {useGetInboxSidebarCounts} from '../inbox/graphql/hooks';
import {useGetUserInboxes} from '../inbox/graphql/hooks/use-get-user-inboxes';

import {AddInboxGroup} from './add-inbox';
import {EngagementSidebarFooter} from './footer';
import {
    getChatLinks,
    getMessageLinks,
    getSettingsLinks,
    getAgentMenuItem,
    getMessengerLinks,
    getAllChannelsLinks,
    getInstagramLinks,
    getWhatsAppLinks,
    getEmailLinks,
} from './helpers';
import {IconWithStatusDot} from './icon-with-status-dot';

import styles from './engagement-sidebar.module.css';

const ROUTES = {
    AGENTS: '/inbox/settings/agents',
} as const;

type Props = ContextRouter & {
    activeFilters: FilterObject[];
    onParamChange: (arg1: any, arg2: boolean) => any;
    onRemoveAllFilters: () => void;
    user: User | null | undefined;
    isSessionUserLoading?: boolean;
    isEmbedded?: boolean;
};

export function EngagementSidebar(props: Props) {
    const [assigneeIds, setAssigneeIds] = React.useState([]);

    const {
        chatUserInboxes,
        smsUserInboxes,
        messengerUserInboxes,
        allChannelsUserInboxes,
        instagramUserInboxes,
        whatsAppUserInboxes,
        emailUserInboxes,
    } = useGetUserInboxes();
    const {chatAgents} = useGetAgents();

    const allChannelsInboxFilters = React.useMemo(
        () => (allChannelsUserInboxes && allChannelsUserInboxes.map((u) => u.id)) || [],
        [allChannelsUserInboxes]
    );

    const chatInboxFilters = React.useMemo(
        () => (chatUserInboxes && chatUserInboxes.map((u) => u.id)) || [],
        [chatUserInboxes]
    );
    const smsInboxFilters = React.useMemo(
        () => (smsUserInboxes && smsUserInboxes.map((u) => u.id)) || [],
        [smsUserInboxes]
    );
    const messengerInboxFilters = React.useMemo(
        () => (messengerUserInboxes && messengerUserInboxes.map((u) => u.id)) || [],
        [messengerUserInboxes]
    );
    const instagramInboxFilters = React.useMemo(
        () => (instagramUserInboxes && instagramUserInboxes.map((u) => u.id)) || [],
        [instagramUserInboxes]
    );
    const whatsAppInboxFilters = React.useMemo(
        () => (whatsAppUserInboxes && whatsAppUserInboxes.map((u) => u.id)) || [],
        [whatsAppUserInboxes]
    );
    const emailInboxFilters = React.useMemo(
        () => (emailUserInboxes && emailUserInboxes.map((u) => u.id)) || [],
        [emailUserInboxes]
    );

    const {inboxSidebarCounts} = useGetInboxSidebarCounts({
        assignees: assigneeIds,
        inboxUserIds: [
            ...chatInboxFilters,
            ...smsInboxFilters,
            ...messengerInboxFilters,
            ...instagramInboxFilters,
            ...allChannelsInboxFilters,
            ...whatsAppInboxFilters,
            ...emailInboxFilters,
        ],
    });

    React.useEffect(() => {
        if (props.activeFilters && props.activeFilters.length > 0) {
            const assigneeFilter = safelyGetFilterByKey(props.activeFilters, 'owner');

            const ids =
                assigneeFilter && assigneeFilter.data
                    ? // @ts-expect-error
                      assigneeFilter.data.map((filter) => filter.data)
                    : [];

            setAssigneeIds(ids || []);
        } else {
            setAssigneeIds([]);
        }
    }, [props.activeFilters]);

    if (props.isSessionUserLoading) {
        return <SecondarySidebar navItems={[]} isLoading={true} />;
    }

    const canAccessSetup = (props.user && props.user.permissions.canAccessSetup) || false;
    const selectedSection = props.location.pathname.split('/')[2];
    const isAgentsSection = Boolean(
        matchPath(props.location.pathname, {
            path: ROUTES.AGENTS,
            exact: true,
        })
    );
    const activeAgentsCount =
        (chatAgents &&
            chatAgents.filter((agent) => agent && agent.agentPresence === 'ONLINE').length) ||
        0;

    const ALL_CHANNELS_ITEM: NavItem = {
        id: 'all',
        label: 'All channels',
        icon: () => (
            <IconWithStatusDot
                // @ts-expect-error
                icon={<ComputerMobileRegularIcon size={16} color={colors.white} />}
                hasUnread={Boolean(inboxSidebarCounts && inboxSidebarCounts.allChannels.hasUnread)}
            />
        ),
        to: '/inbox/all/open',
        submenu: getAllChannelsLinks(
            inboxSidebarCounts && inboxSidebarCounts.allChannels,
            canAccessSetup,
            props.user
        ),
        footer: canAccessSetup && (
            <li className={styles['add-inbox-group']}>
                <AddInboxGroup
                    user={props.user}
                    activeFilters={props.activeFilters}
                    onParamChange={props.onParamChange}
                    onRemoveAllFilters={props.onRemoveAllFilters}
                    filters={allChannelsInboxFilters}
                    userInboxCounts={
                        (inboxSidebarCounts && inboxSidebarCounts.allChannels.userInboxCounts) || []
                    }
                    threadType={null}
                    currentTab={selectedSection}
                />
            </li>
        ),
    };

    const DASHBOARD_ITEM: NavItem = {
        id: 'dashboard',
        label: 'Dashboard',
        // @ts-expect-error
        icon: () => <ChatRegularOptionsIcon size={16} color={colors.white} />,
        to: '/inbox/dashboard',
        hasDivider: true,
    };

    const CHAT_ITEM: NavItem = {
        id: 'chat',
        label: 'Chat',
        icon: () => (
            <IconWithStatusDot
                // @ts-expect-error
                icon={<NavIconChatIcon size={14} color={colors.white} />}
                hasUnread={Boolean(inboxSidebarCounts && inboxSidebarCounts.chat.hasUnread)}
            />
        ),
        to: '/inbox/chat/open',
        submenu: getChatLinks(
            inboxSidebarCounts && inboxSidebarCounts.chat,
            canAccessSetup,
            props.user
        ),
        footer: canAccessSetup && (
            <li className={styles['add-inbox-group']}>
                <AddInboxGroup
                    user={props.user}
                    activeFilters={props.activeFilters}
                    onParamChange={props.onParamChange}
                    onRemoveAllFilters={props.onRemoveAllFilters}
                    filters={chatInboxFilters}
                    userInboxCounts={
                        (inboxSidebarCounts && inboxSidebarCounts.chat.userInboxCounts) || []
                    }
                    threadType='CHAT'
                    currentTab={selectedSection}
                />
            </li>
        ),
    };

    const SMS_ITEM: NavItem = {
        id: 'sms',
        label: 'SMS',
        icon: () => (
            <IconWithStatusDot
                // @ts-expect-error
                icon={<MobilePhoneRegularIcon size={16} color={colors.white} />}
                hasUnread={Boolean(inboxSidebarCounts && inboxSidebarCounts.sms.hasUnread)}
            />
        ),
        to: '/inbox/sms/open',
        submenu: getMessageLinks(
            inboxSidebarCounts && inboxSidebarCounts.sms,
            canAccessSetup,
            props.user
        ),
        footer: canAccessSetup && (
            <li className={styles['add-inbox-group']}>
                <AddInboxGroup
                    user={props.user}
                    activeFilters={props.activeFilters}
                    onParamChange={props.onParamChange}
                    onRemoveAllFilters={props.onRemoveAllFilters}
                    filters={smsInboxFilters}
                    userInboxCounts={
                        (inboxSidebarCounts && inboxSidebarCounts.sms.userInboxCounts) || []
                    }
                    threadType='SMS'
                    currentTab={selectedSection}
                />
            </li>
        ),
    };

    const SHARED_EMAIL_INBOX_ITEM: NavItem = {
        id: 'email',
        label: 'Email',
        icon: () => (
            <IconWithStatusDot
                // @ts-expect-error
                icon={<EnvelopeNewIcon size={16} color={colors.white} />}
                hasUnread={Boolean(inboxSidebarCounts && inboxSidebarCounts.email?.hasUnread)}
            />
        ),
        // @ts-expect-error
        preventAutoSort: true,
        requiresUpgrade: !NutClientConfig.hasSharedEmailInbox,
        to: '/inbox/email/unassigned',
        submenu: getEmailLinks(
            inboxSidebarCounts && inboxSidebarCounts.email,
            canAccessSetup,
            props.user
        ),
        footer: canAccessSetup && (
            <li className={styles['add-inbox-group']}>
                <AddInboxGroup
                    user={props.user}
                    activeFilters={props.activeFilters}
                    onParamChange={props.onParamChange}
                    onRemoveAllFilters={props.onRemoveAllFilters}
                    filters={emailInboxFilters}
                    userInboxCounts={
                        (inboxSidebarCounts && inboxSidebarCounts.email?.userInboxCounts) || []
                    }
                    threadType='EMAIL'
                    currentTab={selectedSection}
                />
            </li>
        ),
    };

    const INSTAGRAM_ITEM: NavItem = {
        id: 'instagram',
        label: 'Instagram',
        icon: () => (
            <IconWithStatusDot
                icon={<InstagramColoredIcon size={12} />}
                hasUnread={Boolean(inboxSidebarCounts && inboxSidebarCounts.instagram.hasUnread)}
            />
        ),
        requiresUpgrade: !NutClientConfig.hasInstagram,
        // @ts-expect-error
        preventAutoSort: true,
        to: '/inbox/instagram/unassigned',
        submenu: getInstagramLinks(
            inboxSidebarCounts && inboxSidebarCounts.instagram,
            canAccessSetup,
            props.user
        ),
        footer: canAccessSetup && (
            <li className={styles['add-inbox-group']}>
                <AddInboxGroup
                    user={props.user}
                    activeFilters={props.activeFilters}
                    onParamChange={props.onParamChange}
                    onRemoveAllFilters={props.onRemoveAllFilters}
                    filters={instagramInboxFilters}
                    userInboxCounts={
                        (inboxSidebarCounts && inboxSidebarCounts.instagram.userInboxCounts) || []
                    }
                    threadType='INSTAGRAM'
                    currentTab={selectedSection}
                />
            </li>
        ),
        hasDivider: !NutClientConfig.hasWhatsAppFF,
    };

    const MESSENGER_ITEM: NavItem = {
        id: 'messenger',
        label: 'Messenger',
        icon: () => (
            <IconWithStatusDot
                icon={<MessengerColoredIcon size={12} />}
                hasUnread={Boolean(inboxSidebarCounts && inboxSidebarCounts.messenger.hasUnread)}
            />
        ),
        requiresUpgrade: !NutClientConfig.hasFacebookMessenger,
        // @ts-expect-error
        preventAutoSort: true,
        to: '/inbox/messenger/unassigned',
        submenu: getMessengerLinks(
            inboxSidebarCounts && inboxSidebarCounts.messenger,
            canAccessSetup,
            props.user
        ),
        footer: canAccessSetup && (
            <li className={styles['add-inbox-group']}>
                <AddInboxGroup
                    user={props.user}
                    activeFilters={props.activeFilters}
                    onParamChange={props.onParamChange}
                    onRemoveAllFilters={props.onRemoveAllFilters}
                    filters={messengerInboxFilters}
                    userInboxCounts={
                        (inboxSidebarCounts && inboxSidebarCounts.messenger.userInboxCounts) || []
                    }
                    threadType='MESSENGER'
                    currentTab={selectedSection}
                />
            </li>
        ),
    };

    const WHATSAPP_ITEM: NavItem = {
        id: 'whatsapp',
        label: 'WhatsApp',
        icon: () => (
            <IconWithStatusDot
                // @ts-expect-error
                icon={<WhatsAppIcon size={12} color={colors.whatsappGreen} />}
                hasUnread={Boolean(inboxSidebarCounts && inboxSidebarCounts.whatsapp.hasUnread)}
            />
        ),
        requiresUpgrade: !NutClientConfig.hasWhatsApp,
        // @ts-expect-error
        preventAutoSort: true,
        to: '/inbox/whatsapp/open',
        submenu: getWhatsAppLinks(
            inboxSidebarCounts && inboxSidebarCounts.whatsapp,
            canAccessSetup,
            props.user
        ),
        footer: canAccessSetup && (
            <li className={styles['add-inbox-group']}>
                <AddInboxGroup
                    user={props.user}
                    activeFilters={props.activeFilters}
                    onParamChange={props.onParamChange}
                    onRemoveAllFilters={props.onRemoveAllFilters}
                    filters={whatsAppInboxFilters}
                    userInboxCounts={
                        (inboxSidebarCounts && inboxSidebarCounts.whatsapp.userInboxCounts) || []
                    }
                    threadType='WHATSAPP'
                    currentTab={selectedSection}
                />
            </li>
        ),
        hasDivider: true,
    };

    const SETTINGS_ITEM: NavItem = {
        id: 'settings',
        label: 'Setup',
        // @ts-expect-error
        icon: () => <WrenchRegularIcon size={16} color={colors.white} />,
        to: 'inbox/settings/chat',
        submenu: getSettingsLinks(canAccessSetup, props.isEmbedded),
    };

    const AGENTS_ITEM: NavItem = {
        id: 'agents',
        label: getAgentMenuItem(activeAgentsCount || 0, isAgentsSection),
        // @ts-expect-error
        icon: () => <AgentsIcon size={16} color={colors.white} />,
        to: '/inbox/settings/agents',
        count: activeAgentsCount,
    };

    // V2 section headers
    const ENGAGEMENT_BAR_HEADER: NavItem = {
        id: 'engagement-bar-header',
        label: 'Engagement bar',
        isTopHeader: true,
    };
    const INBOX_HEADER: NavItem = {
        id: 'inbox-header',
        label: 'Inbox',
        isHeader: true,
    };
    const ADMIN_HEADER: NavItem = {
        id: 'admin-header',
        label: 'Admin',
        isHeader: true,
    };

    // V2 nav items
    const LEADS_ITEM: NavItem = {
        id: 'leads',
        label: 'Leads',
        // @ts-expect-error
        icon: () => <LeadChevronRegularIcon size={14} color={colors.white} />,
        to: '/inbox/leads',
    };

    const CHATBOT_THREADS_ITEM: NavItem = {
        id: 'chatbot-threads',
        label: 'Chatbot threads',
        // @ts-expect-error
        icon: () => <ChatBubbleDoubleRegularIcon size={14} color={colors.white} />,
        to: '/inbox/chatbot-threads',
        submenu: props.isEmbedded
            ? [
                  {
                      id: 'chatbot-threads-all',
                      label: 'All',
                      to: '/inbox/chatbot-threads',
                      icon: TeamIcon,
                  },
              ]
            : undefined,
    };

    // V2 renamed Chat item to "Chats with a human" and excludes chatbot from submenu
    const AGENT_THREADS_ITEM: NavItem = {
        ...CHAT_ITEM,
        id: 'agent-threads',
        label: 'Agent threads',
        // @ts-expect-error
        icon: () => <AgentsIcon size={16} color={colors.white} />,
        submenu: getChatLinks(
            inboxSidebarCounts && inboxSidebarCounts.chat,
            canAccessSetup,
            props.user,
            {excludeChatbot: true}
        ),
    };

    const REPORTS_ITEM: NavItem = {
        id: 'reports',
        label: 'Reports',
        to: '/inbox/reports',
        hasDivider: true,
        // @ts-expect-error
        icon: () => <ViewExploreIcon size={16} color={colors.white} />,
        submenu: [
            {
                id: 'engagement',
                label: 'Engagement',
                to: '/inbox/reports/engagement-report',
            },
        ],
    };

    let navItems = [];
    if (props.isEmbedded) {
        if (NutClientConfig.hasEngagementBarV2FF) {
            navItems = [
                ALL_CHANNELS_ITEM,
                CHATBOT_THREADS_ITEM,
                AGENT_THREADS_ITEM,
                SMS_ITEM,
                MESSENGER_ITEM,
                INSTAGRAM_ITEM,
                WHATSAPP_ITEM,
                REPORTS_ITEM,
            ];
        } else {
            navItems = [
                ALL_CHANNELS_ITEM,
                CHAT_ITEM,
                SMS_ITEM,
                MESSENGER_ITEM,
                INSTAGRAM_ITEM,
                WHATSAPP_ITEM,
            ];
        }

        if (canAccessSetup) {
            navItems.push(SETTINGS_ITEM);
        }
    } else if (NutClientConfig.hasEngagementBarV2FF) {
        // V2 structure with section headers
        navItems = [
            DASHBOARD_ITEM,
            ENGAGEMENT_BAR_HEADER,
            LEADS_ITEM,
            CHATBOT_THREADS_ITEM,
            INBOX_HEADER,
            ALL_CHANNELS_ITEM,
            AGENT_THREADS_ITEM,
            SMS_ITEM,
            ...(NutClientConfig.hasSharedEmailInboxFF ? [SHARED_EMAIL_INBOX_ITEM] : []),
            MESSENGER_ITEM,
            INSTAGRAM_ITEM,
            WHATSAPP_ITEM,
        ];

        if (canAccessSetup) {
            navItems.push(ADMIN_HEADER, SETTINGS_ITEM, AGENTS_ITEM);
        }
    } else {
        const nonEmbeddedItems = [
            DASHBOARD_ITEM,
            ALL_CHANNELS_ITEM,
            CHAT_ITEM,
            SMS_ITEM,
            ...(NutClientConfig.hasSharedEmailInboxFF ? [SHARED_EMAIL_INBOX_ITEM] : []),
            MESSENGER_ITEM,
            INSTAGRAM_ITEM,
            WHATSAPP_ITEM,
        ];

        if (canAccessSetup) {
            nonEmbeddedItems.push(SETTINGS_ITEM, AGENTS_ITEM);
        }

        navItems = nonEmbeddedItems;
    }

    return (
        <SecondarySidebar
            navItems={navItems}
            footer={<EngagementSidebarFooter isEmbedded={Boolean(props.isEmbedded)} />}
            isEmbedded={props.isEmbedded}
        />
    );
}
